import { useQuery } from "@tanstack/react-query";
import { Link } from "raviger";
import { useTranslation } from "react-i18next";

import Page from "@/components/Common/Page";
import { ThemeToggle } from "@/components/Common/ThemeToggle";

import useAuthUser from "@/hooks/useAuthUser";

import { getPermissions } from "@/common/Permissions";

import {
  DashboardLinkContext,
  processCustomDashboardLinks,
} from "@/Utils/dashboardLinks";
import query from "@/Utils/request/query";
import { usePermissions } from "@/context/PermissionContext";
import facilityApi from "@/types/facility/facilityApi";
import careConfig from "@careConfig";
import { Box, Calendar, CreditCard, Search, Users } from "lucide-react";

interface FacilityOverviewProps {
  facilityId: string;
}

export function FacilityOverview({ facilityId }: FacilityOverviewProps) {
  const { t } = useTranslation();
  const user = useAuthUser();
  const { hasPermission } = usePermissions();

  const { data: facilityData } = useQuery({
    queryKey: ["facility", facilityId],
    queryFn: query(facilityApi.get, {
      pathParams: { facilityId },
    }),
  });

  const {
    canViewSchedule,
    canListEncounters,
    canViewPatients,
    canReadHealthcareService,
    canWriteHealthcareService,
    canReadAccount,
    canReadInvoice,
    canReadPaymentReconciliation,
    canAccessPractitionerWorkspace,
  } = getPermissions(hasPermission, facilityData?.permissions ?? []);
  const canUseBilling =
    canReadAccount || canReadInvoice || canReadPaymentReconciliation;

  // Default shortcuts
  const defaultShortcuts = [
    {
      title: t("my_schedules"),
      description: t("manage_my_schedule"),
      icon: Calendar,
      href: `/facility/${facilityId}/users/${user?.username}/availability`,
      visible: canViewSchedule,
    },
    {
      title: t("encounters"),
      description: t("manage_facility_users"),
      icon: Users,
      href: `/facility/${facilityId}/encounters/patients/${careConfig.defaultEncounterType || "all"}`,
      visible: canListEncounters,
    },
    {
      title: t("services"),
      description: t("view_services"),
      icon: Box,
      href: `/facility/${facilityId}/services`,
      // Allow superusers to see services even if they also have practitioner workspace access
      visible:
        (canReadHealthcareService || canWriteHealthcareService) &&
        (!canAccessPractitionerWorkspace || user?.is_superuser),
    },
    {
      title: t("billing"),
      description: t("view_and_manage_account_payments"),
      icon: CreditCard,
      href: `/facility/${facilityId}/billing/account`,
      // Allow superusers to see billing even if they also have practitioner workspace access
      visible:
        canUseBilling &&
        (!canAccessPractitionerWorkspace || user?.is_superuser),
    },
    {
      title: t("search_patients"),
      description: t("search_patients_description"),
      icon: Search,
      href: `/facility/${facilityId}/patients`,
      visible: canViewPatients,
    },
  ];

  // Process custom dashboard links from environment
  const iconMap = {
    Calendar,
    Users,
    Box,
    CreditCard,
    Search,
  };

  const context: DashboardLinkContext = {
    facilityId,
    userId: user?.id,
    username: user?.username,
  };

  const customDashboardLinks = processCustomDashboardLinks(
    careConfig.customShortcuts,
    context,
    iconMap,
  );

  // Combine default and custom dashboard links
  const shortcuts = [...defaultShortcuts, ...customDashboardLinks];

  // For adding dynamic styling classes for icons in quick actions
  const getColorClasses = (icon: any) => {
    if (icon === Calendar) {
      return {
        iconBg: "from-purple-100 to-purple-50",
        iconText: "text-purple-600",
        arrow: "group-hover:text-purple-600",
      };
    } else if (icon === Users) {
      return {
        iconBg: "from-indigo-100 to-indigo-50",
        iconText: "text-indigo-600",
        arrow: "group-hover:text-indigo-600",
      };
    } else if (icon === Box) {
      return {
        iconBg: "from-orange-100 to-orange-50",
        iconText: "text-orange-600",
        arrow: "group-hover:text-orange-600",
      };
    }
    return {
      iconBg: "from-blue-100 to-blue-50",
      iconText: "text-[#04568C]",
      arrow: "group-hover:text-[#04568C]",
    };
  };

  return (
    <Page title="">
      <div className="container mx-auto space-y-8">
        {/* Welcome Header */}
        <div className="rounded-lg">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-50">
                {t("hey_user", {
                  user: [user.prefix, user.first_name]
                    .filter(Boolean)
                    .join(" "),
                })}
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                {t("welcome_back_to_hospital_dashboard")}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle className="rounded-full border border-gray-200 bg-white/80 shadow-sm backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/80" />
              {/* AayushNet Logo - Hidden on mobile */}
              <div className="hidden items-center gap-0 md:flex">
              <img
                src="/images/aayushnet_logo_light.png"
                className="h-12"
                alt="AayushNet logo"
              />
              <span
                className="text-3xl font-black tracking-wider"
                style={{
                  fontFamily: "'Zen Kaku Gothic Antique', sans-serif",
                }}
              >
                <span
                  style={{
                    background:
                      "linear-gradient(to bottom, #5ba3ff 0%, #002d6b 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    display: "inline-block",
                    paddingBottom: "2px",
                  }}
                >
                  Aayush
                </span>
                <span
                  style={{
                    background:
                      "linear-gradient(to bottom, #66ff99 0%, #1a7a3f 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    display: "inline-block",
                    paddingBottom: "2px",
                  }}
                >
                  Net
                </span>
              </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions Section */}
        <div className="">
          <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-gray-50">
            {t("quick_actions")}
          </h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {shortcuts
              .filter((shortcut) => shortcut.visible)
              .map((shortcut) => {
                const colorClasses = getColorClasses(shortcut.icon);
                return (
                  <Link
                    key={shortcut.href}
                    href={shortcut.href}
                    className="group"
                  >
                    <div className="flex h-full w-full cursor-pointer gap-x-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-[0_4px_16px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_36px_rgba(0,0,0,0.20)] dark:border-gray-800 dark:bg-gray-950">
                      {/* Icon Avatar */}
                      <div
                        className={`w-14 h-14 shrink-0 rounded-full bg-linear-to-br flex items-center justify-center text-2xl font-bold ${colorClasses.iconBg} ${colorClasses.iconText}`}
                      >
                        <shortcut.icon className="size-7" />
                      </div>

                      <div className="flex flex-col justify-center gap-y-1">
                        {/* Title */}
                        <h3
                          className={`text-base font-semibold ${colorClasses.iconText}`}
                        >
                          {shortcut.title}
                        </h3>

                        {/* Description */}
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {shortcut.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    </Page>
  );
}

export default FacilityOverview;
